import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { map, take, takeUntil } from 'rxjs/operators';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';

import { environment } from '@environments/environment';
import { ReportTemplate, RunReportModalData } from '@models/report-template.model';
import { Provider } from '@models/provider.model';
import { ReportingActions } from '@store/actions/reporting/reporting.actions';
import { selectProviders as selectClinicianProviders } from '@store/selectors/clinician-details/clinician-details.selectors';
import { selectIsRunning, selectIsBulkRunning } from '@store/selectors/reporting/reporting.selectors';
import { selectActiveUser } from '@store/selectors/user/user.selectors';

interface ProviderOption {
  id: string;
  label: string;
  email: string | undefined;
}

interface EmailOption {
  value: string;
  label: string;
}

const PROVIDER_ORG_VALUE = 'Organization';
const PROVIDER_SELECT_ALL = 'SELECT_ALL';

@Component({
  selector: 'app-run-report-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    MatChipsModule,
    DatePipe,
    NgxMatSelectSearchModule,
  ],
  templateUrl: './run-report-modal.component.html',
  styleUrl: './run-report-modal.component.scss',
})
export class RunReportModalComponent implements OnInit, OnDestroy {
  private readonly store = inject(Store);
  private readonly fb = inject(FormBuilder);
  private readonly dialogRef = inject(MatDialogRef<RunReportModalComponent>);
  private readonly actions$ = inject(Actions);
  private readonly snackBar = inject(MatSnackBar);
  readonly data: RunReportModalData = inject(MAT_DIALOG_DATA);

  private readonly destroy$ = new Subject<void>();

  readonly template: ReportTemplate = this.data.template;
  readonly today = new Date();

  form: FormGroup = this.fb.group({
    providerIds: [[PROVIDER_ORG_VALUE], Validators.required],
    emailRecipients: [[]],
  });

  // Provider dropdown
  providerOptions: ProviderOption[] = [{ id: PROVIDER_ORG_VALUE, label: 'Organization', email: undefined }];
  filteredProviderOptions: ProviderOption[] = [{ id: PROVIDER_ORG_VALUE, label: 'Organization', email: undefined }];
  providerSearchControl = new FormControl<string>('');
  protected allProviders: ProviderOption[] = [];
  private lastProviderSelection: string[] = [PROVIDER_ORG_VALUE];

  // Email dropdown
  allEmailOptions: EmailOption[] = [];
  filteredEmailOptions: EmailOption[] = [];
  emailSearchControl = new FormControl<string>('');
  private lastEmailSelection: string[] = [];

  // Custom email entry
  customEmailControl = new FormControl<string>('', [Validators.email]);
  customEmails: string[] = [];

  notifyEachProvider = false;

  readonly allowProviderEmailRecipients = environment.allowProviderEmailRecipients;

  isRunning$ = this.store.select(selectIsRunning);
  isBulkRunning$ = this.store.select(selectIsBulkRunning);
  isProvidersReady$ = this.store.select(selectClinicianProviders).pipe(
    map((providers) => providers.length > 0)
  );

  userEmail = '';

  get isOrganizationSelected(): boolean {
    const providerIds: string[] = this.form.get('providerIds')?.value ?? [];
    return providerIds.includes(PROVIDER_ORG_VALUE);
  }

  get isBulkMode(): boolean {
    const providerIds: string[] = (this.form.get('providerIds')?.value ?? []).filter((v: string) => v !== PROVIDER_SELECT_ALL);
    return !providerIds.includes(PROVIDER_ORG_VALUE) && providerIds.length > 1;
  }

  get showNotifyEachProvider(): boolean {
    return this.isBulkMode;
  }

  get selectedProviderCount(): number {
    const providerIds: string[] = this.form.get('providerIds')?.value ?? [];
    return providerIds.filter((id) => id !== PROVIDER_ORG_VALUE && id !== PROVIDER_SELECT_ALL).length;
  }

  get triggerLabel(): string {
    const values: string[] = (this.form.get('providerIds')?.value ?? []).filter((v: string) => v !== PROVIDER_SELECT_ALL);
    if (values.includes(PROVIDER_ORG_VALUE)) return 'Organization';
    if (values.length === 0) return '';
    if (values.length === 1) return this.allProviders.find((p) => p.id === values[0])?.label ?? values[0];
    return `${values.length} providers`;
  }

  get isAllSelected(): boolean {
    return (this.form.get('emailRecipients')?.value ?? []).includes('ALL');
  }

  get dropdownSelectedEmails(): string[] {
    const selected: string[] = this.form.get('emailRecipients')?.value ?? [];
    return selected.filter((v: string) => v !== 'ALL');
  }

  get hasAnyEmails(): boolean {
    return this.isAllSelected || this.dropdownSelectedEmails.length > 0 || this.customEmails.length > 0;
  }

  get resolvedEmails(): string[] {
    const selected: string[] = this.form.get('emailRecipients')?.value ?? [];
    const fromDropdown = selected.includes('ALL')
      ? this.allEmailOptions.map((o) => o.value)
      : selected.filter(Boolean);
    return [...new Set([...fromDropdown, ...this.customEmails])];
  }

  ngOnInit(): void {
    this.actions$.pipe(
      ofType(ReportingActions.bulkRunReportSuccess),
      take(1),
      takeUntil(this.destroy$)
    ).subscribe(({ queuedProviderIds }) => {
      const count = queuedProviderIds.length;
      this.dialogRef.close();
      this.snackBar.open(
        `${count} report${count === 1 ? '' : 's'} queued — check the results table shortly.`,
        'Dismiss',
        { duration: 6000 }
      );
    });

    this.store.select(selectActiveUser)
      .pipe(takeUntil(this.destroy$))
      .subscribe((user) => {
        this.userEmail = user.email ?? '';
        if (this.userEmail) {
          this.form.get('emailRecipients')?.setValue([this.userEmail]);
          this.lastEmailSelection = [this.userEmail];
        }
      });

    this.store.select(selectClinicianProviders)
      .pipe(takeUntil(this.destroy$))
      .subscribe((providers) => {
        const activeProviders = (providers ?? []).filter((p: Provider) => p.active === 1);
        this.allProviders = [
          { id: PROVIDER_ORG_VALUE, label: 'Organization', email: undefined },
          ...activeProviders.map((p: Provider) => ({
            id: String(p.id),
            label: `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim(),
            email: p.providerUserEmailAddress,
          })),
        ];
        this.providerOptions = this.allProviders;
        this.allEmailOptions = this.allowProviderEmailRecipients
          ? activeProviders
              .filter((p: Provider) => p.providerUserEmailAddress)
              .map((p: Provider) => ({
                value: p.providerUserEmailAddress!,
                label: `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim(),
              }))
          : [];
        this.applyProviderSearch(this.providerSearchControl.value ?? '');
        this.applyEmailSearch(this.emailSearchControl.value ?? '');
      });

    this.providerSearchControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((text) => this.applyProviderSearch(text ?? ''));

    this.emailSearchControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((text) => this.applyEmailSearch(text ?? ''));
  }

  private applyProviderSearch(text: string): void {
    const q = text.toLowerCase();
    this.filteredProviderOptions = this.providerOptions.filter(
      (o) => o.id === PROVIDER_ORG_VALUE || o.label.toLowerCase().includes(q)
    );
  }

  private applyEmailSearch(text: string): void {
    const q = text.toLowerCase();
    this.filteredEmailOptions = this.allEmailOptions.filter(
      (o) => o.label.toLowerCase().includes(q) || o.value.toLowerCase().includes(q)
    );
  }

  onProviderSelectionChange(values: string[]): void {
    const individualIds = this.allProviders.filter((o) => o.id !== PROVIDER_ORG_VALUE).map((o) => o.id);
    const hadSelectAll = this.lastProviderSelection.includes(PROVIDER_SELECT_ALL);
    const hasSelectAll = values.includes(PROVIDER_SELECT_ALL);

    let newValue: string[];

    if (hasSelectAll && !hadSelectAll) {
      newValue = [PROVIDER_SELECT_ALL, ...individualIds];
    } else if (!hasSelectAll && hadSelectAll) {
      newValue = [];
    } else {
      const withoutSentinel = values.filter((v) => v !== PROVIDER_SELECT_ALL);
      const hadAll = this.lastProviderSelection.includes(PROVIDER_ORG_VALUE);
      const hasAll = withoutSentinel.includes(PROVIDER_ORG_VALUE);

      if (hasAll && !hadAll) {
        newValue = [PROVIDER_ORG_VALUE];
      } else if (hasAll && withoutSentinel.length > 1) {
        const individuals = withoutSentinel.filter((v) => v !== PROVIDER_ORG_VALUE);
        const allAreSelected = individualIds.length > 0 && individualIds.every((id) => individuals.includes(id));
        newValue = allAreSelected ? [PROVIDER_SELECT_ALL, ...individuals] : individuals;
      } else {
        const allAreSelected = individualIds.length > 0 && individualIds.every((id) => withoutSentinel.includes(id));
        newValue = allAreSelected ? [PROVIDER_SELECT_ALL, ...withoutSentinel] : withoutSentinel;
      }
    }

    this.form.get('providerIds')?.setValue(newValue, { emitEvent: false });
    this.lastProviderSelection = newValue;
    if (!this.showNotifyEachProvider) {
      this.notifyEachProvider = false;
    }
  }

  onEmailSelectionChange(values: string[]): void {
    const hadAll = this.lastEmailSelection.includes('ALL');
    const hasAll = values.includes('ALL');
    if (hasAll && !hadAll) {
      this.form.get('emailRecipients')?.setValue(['ALL'], { emitEvent: false });
    } else if (hasAll && values.length > 1) {
      this.form.get('emailRecipients')?.setValue(values.filter((v) => v !== 'ALL'), { emitEvent: false });
    }
    this.lastEmailSelection = this.form.get('emailRecipients')?.value ?? [];
  }

  addCustomEmail(): void {
    const email = (this.customEmailControl.value ?? '').trim();
    if (!email || this.customEmailControl.invalid) return;
    if (!this.customEmails.includes(email) && !this.resolvedEmails.includes(email)) {
      this.customEmails = [...this.customEmails, email];
    }
    this.customEmailControl.setValue('');
  }

  removeEmail(email: string): void {
    if (this.customEmails.includes(email)) {
      this.customEmails = this.customEmails.filter((e) => e !== email);
      return;
    }
    const current: string[] = this.form.get('emailRecipients')?.value ?? [];
    if (current.includes('ALL')) {
      const expanded = this.allEmailOptions.map((o) => o.value).filter((e) => e !== email);
      this.form.get('emailRecipients')?.setValue(expanded);
    } else {
      this.form.get('emailRecipients')?.setValue(current.filter((e) => e !== email));
    }
    this.lastEmailSelection = this.form.get('emailRecipients')?.value ?? [];
  }

  clearAllEmails(): void {
    this.form.get('emailRecipients')?.setValue([]);
    this.customEmails = [];
    this.lastEmailSelection = [];
  }

  onRun(): void {
    const providerIds: string[] = this.form.get('providerIds')?.value ?? [];

    if (!this.isBulkMode) {
      const actualIds = providerIds.filter((id) => id !== PROVIDER_SELECT_ALL);
      const providerId = actualIds.includes(PROVIDER_ORG_VALUE) ? PROVIDER_ORG_VALUE : actualIds[0];
      this.store.dispatch(
        ReportingActions.runReport({
          params: { providerId, emails: this.resolvedEmails },
        })
      );
      this.dialogRef.close();
      return;
    }

    const providers = providerIds.filter((id) => id !== PROVIDER_SELECT_ALL).map((id) => ({
      id,
      email: this.notifyEachProvider
        ? (this.allProviders.find((p) => p.id === id)?.email ?? null)
        : null,
    }));
    this.store.dispatch(
      ReportingActions.bulkRunReport({
        providers,
        emailRecipients: this.resolvedEmails.length > 0 ? this.resolvedEmails : null,
      })
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
